import { useState, useEffect } from 'react';
import {
  Shield,
  AlertTriangle,
  Bell,
  CheckCircle,
  XCircle,
  Settings,
  Plus,
  Clock,
} from 'lucide-react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

// ─── Types ────────────────────────────────────────────────────

interface BreachResponseRule {
  id: string;
  name: string;
  is_active: boolean;
  trigger_severities: string[];
  trigger_monitor_types: string[];
  actions: {
    create_psa_ticket: boolean;
    psa_board_id?: string;
    psa_priority?: string;
    notify_user: boolean;
    notification_template?: string;
    custom_message?: string;
    emit_bus_event: boolean;
  };
  cooldown_hours: number;
  created_at: string;
  updated_at: string;
}

interface ActionTaken {
  action: string;
  success: boolean;
  result?: string;
  error?: string;
  executed_at: string;
}

interface BreachResponseIncident {
  id: string;
  rule_name: string;
  monitor_value: string;
  severity: string;
  status: 'pending' | 'actioned' | 'dismissed' | 'failed';
  actions_taken: ActionTaken[];
  psa_ticket_id?: string;
  dismissed_at?: string;
  resolved_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface RulesResponse {
  data: BreachResponseRule[];
}

interface IncidentsResponse {
  data: BreachResponseIncident[];
  hasMore: boolean;
}

interface SummaryResponse {
  data: {
    period_days: number;
    total_incidents: number;
    actioned: number;
    pending: number;
    failed: number;
    dismissed: number;
  };
}

// ─── Helpers ─────────────────────────────────────────────────

function severityBadge(severity: string) {
  const map: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-blue-100 text-blue-700',
  };
  return map[severity] ?? 'bg-gray-100 text-gray-700';
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    actioned: 'bg-emerald-50 text-emerald-700',
    pending: 'bg-yellow-50 text-yellow-700',
    failed: 'bg-red-50 text-red-700',
    dismissed: 'bg-gray-100 text-gray-500',
  };
  return map[status] ?? 'bg-gray-100 text-gray-700';
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'actioned') return <CheckCircle className="h-4 w-4 text-emerald-600" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-red-500" />;
  if (status === 'dismissed') return <Shield className="h-4 w-4 text-gray-400" />;
  return <Clock className="h-4 w-4 text-yellow-500" />;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Create Rule Modal ────────────────────────────────────────

interface CreateRuleModalProps {
  onClose: () => void;
  onCreated: () => void;
}

const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
const MONITOR_TYPES = ['email', 'domain', 'keyword'] as const;

function CreateRuleModal({ onClose, onCreated }: CreateRuleModalProps) {
  const [name, setName] = useState('');
  const [triggerSeverities, setTriggerSeverities] = useState<string[]>(['critical', 'high']);
  const [triggerMonitorTypes, setTriggerMonitorTypes] = useState<string[]>(['email', 'domain', 'keyword']);
  const [createTicket, setCreateTicket] = useState(true);
  const [psaBoardId, setPsaBoardId] = useState('');
  const [psaPriority, setPsaPriority] = useState('high');
  const [notifyUser, setNotifyUser] = useState(true);
  const [emitBusEvent, setEmitBusEvent] = useState(true);
  const [cooldownHours, setCooldownHours] = useState(24);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleSeverity(s: string) {
    setTriggerSeverities(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s],
    );
  }

  function toggleMonitorType(t: string) {
    setTriggerMonitorTypes(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t],
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Rule name is required'); return; }
    if (triggerSeverities.length === 0) { setError('Select at least one severity level'); return; }
    if (triggerMonitorTypes.length === 0) { setError('Select at least one monitor type'); return; }

    setSubmitting(true);
    setError(null);
    try {
      await post('/protect/breach-response', {
        name: name.trim(),
        is_active: true,
        trigger_severities: triggerSeverities,
        trigger_monitor_types: triggerMonitorTypes,
        actions: {
          create_psa_ticket: createTicket,
          psa_board_id: psaBoardId.trim() || undefined,
          psa_priority: psaPriority,
          notify_user: notifyUser,
          notification_template: 'default',
          emit_bus_event: emitBusEvent,
        },
        cooldown_hours: cooldownHours,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create rule');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Create Breach Response Rule</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Rule name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule name</label>
            <input
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Critical breach auto-response"
            />
          </div>

          {/* Trigger severities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Trigger on severity</label>
            <div className="flex flex-wrap gap-2">
              {SEVERITIES.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSeverity(s)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    triggerSeverities.includes(s)
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Trigger monitor types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Trigger on monitor type</label>
            <div className="flex flex-wrap gap-2">
              {MONITOR_TYPES.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleMonitorType(t)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    triggerMonitorTypes.includes(t)
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <fieldset className="border border-gray-200 rounded-lg p-4 space-y-3">
            <legend className="text-sm font-medium text-gray-700 px-1">Actions to execute</legend>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={createTicket}
                onChange={e => setCreateTicket(e.target.checked)}
                className="h-4 w-4 text-emerald-600 rounded"
              />
              <span className="text-sm text-gray-700">Create PSA ticket</span>
            </label>
            {createTicket && (
              <div className="ml-7 space-y-2">
                <input
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={psaBoardId}
                  onChange={e => setPsaBoardId(e.target.value)}
                  placeholder="PSA Board ID (optional)"
                />
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={psaPriority}
                  onChange={e => setPsaPriority(e.target.value)}
                >
                  <option value="low">Priority: Low</option>
                  <option value="medium">Priority: Medium</option>
                  <option value="high">Priority: High</option>
                  <option value="critical">Priority: Critical</option>
                </select>
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyUser}
                onChange={e => setNotifyUser(e.target.checked)}
                className="h-4 w-4 text-emerald-600 rounded"
              />
              <span className="text-sm text-gray-700">Send notification email to affected user</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={emitBusEvent}
                onChange={e => setEmitBusEvent(e.target.checked)}
                className="h-4 w-4 text-emerald-600 rounded"
              />
              <span className="text-sm text-gray-700">Emit platform bus event</span>
            </label>
          </fieldset>

          {/* Cooldown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cooldown (hours) — avoid re-triggering the same monitor within this window
            </label>
            <input
              type="number"
              min="0"
              max="720"
              className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={cooldownHours}
              onChange={e => setCooldownHours(parseInt(e.target.value, 10) || 0)}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
            >
              {submitting ? 'Creating…' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Incident Row ─────────────────────────────────────────────

interface IncidentRowProps {
  incident: BreachResponseIncident;
  onAction: () => void;
}

function IncidentRow({ incident, onAction }: IncidentRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [actioning, setActioning] = useState(false);

  async function handleDismiss() {
    setActioning(true);
    try {
      await post(`/protect/breach-response/${incident.id}?action=dismiss`, {});
      onAction();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to dismiss');
    } finally {
      setActioning(false);
    }
  }

  async function handleResolve() {
    setActioning(true);
    try {
      await post(`/protect/breach-response/${incident.id}?action=resolve`, {});
      onAction();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resolve');
    } finally {
      setActioning(false);
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <StatusIcon status={incident.status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-gray-900 truncate">{incident.monitor_value}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${severityBadge(incident.severity)}`}>
              {incident.severity}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(incident.status)}`}>
              {incident.status}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span>Rule: {incident.rule_name}</span>
            <span>{fmtDate(incident.created_at)}</span>
            {incident.psa_ticket_id && <span>Ticket: {incident.psa_ticket_id}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-2">
          {incident.status === 'pending' || incident.status === 'failed' ? (
            <>
              <button
                className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium"
                onClick={e => { e.stopPropagation(); handleResolve(); }}
                disabled={actioning}
              >
                Mark resolved
              </button>
              <button
                className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 font-medium"
                onClick={e => { e.stopPropagation(); handleDismiss(); }}
                disabled={actioning}
              >
                Dismiss
              </button>
            </>
          ) : null}
          <span className="text-gray-400 text-xs">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Actions taken</p>
          {incident.actions_taken.length === 0 ? (
            <p className="text-xs text-gray-400">No actions recorded yet</p>
          ) : (
            <ul className="space-y-1.5">
              {incident.actions_taken.map((a, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs">
                  {a.success
                    ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    : <XCircle className="h-3.5 w-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                  }
                  <span className="text-gray-600">
                    <span className="font-medium">{a.action}</span>
                    {a.result && <span className="text-gray-500"> — {a.result}</span>}
                    {a.error && <span className="text-red-500"> — {a.error}</span>}
                    <span className="text-gray-400 ml-1">({fmtDate(a.executed_at)})</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Rules Tab ────────────────────────────────────────────────

interface RulesTabProps {
  rules: BreachResponseRule[];
  loading: boolean;
  onToggleActive: (rule: BreachResponseRule) => void;
  onCreateRule: () => void;
}

function RulesTab({ rules, loading, onToggleActive, onCreateRule }: RulesTabProps) {
  if (loading) {
    return <p className="text-sm text-gray-500 py-8 text-center">Loading rules…</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">{rules.length} rule{rules.length !== 1 ? 's' : ''} configured</p>
        <button
          onClick={onCreateRule}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4" />
          New Rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <Settings className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-600">No rules configured</p>
          <p className="text-xs text-gray-400 mt-1">Create a rule to start automating breach responses</p>
          <button
            onClick={onCreateRule}
            className="mt-4 flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 mx-auto"
          >
            <Plus className="h-4 w-4" />
            Create first rule
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => (
            <div key={rule.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onToggleActive(rule)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                      rule.is_active ? 'bg-emerald-500' : 'bg-gray-300'
                    }`}
                    title={rule.is_active ? 'Disable rule' : 'Enable rule'}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        rule.is_active ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <div>
                    <span className="font-medium text-sm text-gray-900">{rule.name}</span>
                    {!rule.is_active && (
                      <span className="ml-2 text-xs text-gray-400">(inactive)</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-500">
                <div>
                  <span className="font-medium text-gray-600">Triggers: </span>
                  {rule.trigger_severities.join(', ')}
                </div>
                <div>
                  <span className="font-medium text-gray-600">Monitor types: </span>
                  {rule.trigger_monitor_types.join(', ')}
                </div>
                <div>
                  <span className="font-medium text-gray-600">PSA ticket: </span>
                  {rule.actions.create_psa_ticket ? 'Yes' : 'No'}
                  {rule.actions.create_psa_ticket && rule.actions.psa_board_id && (
                    <span className="text-gray-400"> (Board: {rule.actions.psa_board_id})</span>
                  )}
                </div>
                <div>
                  <span className="font-medium text-gray-600">Notify user: </span>
                  {rule.actions.notify_user ? 'Yes' : 'No'}
                </div>
                <div>
                  <span className="font-medium text-gray-600">Bus event: </span>
                  {rule.actions.emit_bus_event ? 'Yes' : 'No'}
                </div>
                <div>
                  <span className="font-medium text-gray-600">Cooldown: </span>
                  {rule.cooldown_hours}h
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function BreachResponsePage() {
  const [activeTab, setActiveTab] = useState<'incidents' | 'rules'>('incidents');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [incidentStatus, setIncidentStatus] = useState('');
  const [rulesVersion, setRulesVersion] = useState(0);
  const [incidentsVersion, setIncidentsVersion] = useState(0);

  const { data: summaryData, loading: summaryLoading } = useApi<SummaryResponse>(
    `/protect/breach-response?action=summary`,
  );

  const incidentQuery = `/protect/breach-response${incidentStatus ? `?status=${incidentStatus}` : ''}#${incidentsVersion}`;
  const { data: incidentsData, loading: incidentsLoading, error: incidentsError, refresh: refreshIncidents } =
    useApi<IncidentsResponse>(incidentQuery.replace(`#${incidentsVersion}`, ''));

  const { data: rulesData, loading: rulesLoading, refresh: refreshRules } =
    useApi<RulesResponse>(`/protect/breach-response?action=rules`);

  // Re-fetch when versions change
  useEffect(() => { refreshIncidents(); }, [incidentsVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { refreshRules(); }, [rulesVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = summaryData?.data;
  const incidents = incidentsData?.data ?? [];
  const rules = rulesData?.data ?? [];

  async function handleToggleRule(rule: BreachResponseRule) {
    try {
      await post('/protect/breach-response', {
        action: 'update_rule',
        id: rule.id,
        is_active: !rule.is_active,
      });
      setRulesVersion(v => v + 1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update rule');
    }
  }

  function handleIncidentAction() {
    setIncidentsVersion(v => v + 1);
  }

  function handleRuleCreated() {
    setShowCreateModal(false);
    setRulesVersion(v => v + 1);
    setActiveTab('rules');
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Bell className="h-7 w-7 text-emerald-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Breach Response</h1>
          <p className="text-sm text-gray-500">Automated response rules for dark web findings</p>
        </div>
      </div>

      {/* Summary stats bar */}
      {summaryLoading ? (
        <div className="grid grid-cols-5 gap-4 animate-pulse">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-gray-100 rounded-lg h-20" />
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <StatCard
            label="Total (30d)"
            value={summary.total_incidents}
            colorClass="text-gray-900"
            icon={<AlertTriangle className="h-5 w-5 text-gray-400" />}
          />
          <StatCard
            label="Actioned"
            value={summary.actioned}
            colorClass="text-emerald-700"
            icon={<CheckCircle className="h-5 w-5 text-emerald-400" />}
          />
          <StatCard
            label="Pending"
            value={summary.pending}
            colorClass="text-yellow-700"
            icon={<Clock className="h-5 w-5 text-yellow-400" />}
          />
          <StatCard
            label="Failed"
            value={summary.failed}
            colorClass="text-red-700"
            icon={<XCircle className="h-5 w-5 text-red-400" />}
          />
          <StatCard
            label="Dismissed"
            value={summary.dismissed}
            colorClass="text-gray-500"
            icon={<Shield className="h-5 w-5 text-gray-400" />}
          />
        </div>
      ) : null}

      {/* Tab navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {(['incidents', 'rules'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'incidents' && incidents.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full bg-gray-100 text-xs text-gray-600">
                  {incidents.length}
                </span>
              )}
              {tab === 'rules' && rules.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full bg-emerald-50 text-xs text-emerald-700">
                  {rules.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'incidents' && (
        <div>
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm font-medium text-gray-700">Filter by status:</label>
            <select
              value={incidentStatus}
              onChange={e => setIncidentStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="actioned">Actioned</option>
              <option value="failed">Failed</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>

          {incidentsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : incidentsError ? (
            <div className="text-sm text-red-600 bg-red-50 rounded-lg p-4">
              Error loading incidents: {incidentsError}
            </div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <Bell className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-600">No incidents found</p>
              <p className="text-xs text-gray-400 mt-1">
                {incidentStatus
                  ? `No ${incidentStatus} incidents`
                  : 'Incidents will appear here when breach response rules trigger'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {incidents.map(incident => (
                <IncidentRow
                  key={incident.id}
                  incident={incident}
                  onAction={handleIncidentAction}
                />
              ))}
              {incidentsData?.hasMore && (
                <p className="text-xs text-gray-400 text-center pt-2">
                  Showing first 50 incidents. Use filters to narrow results.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'rules' && (
        <RulesTab
          rules={rules}
          loading={rulesLoading}
          onToggleActive={handleToggleRule}
          onCreateRule={() => setShowCreateModal(true)}
        />
      )}

      {/* Create Rule Modal */}
      {showCreateModal && (
        <CreateRuleModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleRuleCreated}
        />
      )}
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────

function StatCard({
  label,
  value,
  colorClass,
  icon,
}: {
  label: string;
  value: number;
  colorClass: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        {icon}
      </div>
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
    </div>
  );
}
